import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO — reusable per-route metadata component.
 *
 * Public marketing routes should pass `noindex={false}` (default) plus
 * per-page title, description, canonical URL, and (optionally) an OG image.
 *
 * Authenticated / portal routes should pass `noindex` so search engines and
 * archive services skip them entirely.
 *
 * NOTE: The homepage JSON-LD @graph is baked directly into
 * `/frontend/public/index.html` so it is present in the initial server-returned
 * HTML *before* React hydrates. This component therefore does NOT inject
 * JSON-LD — that avoids duplicate `application/ld+json` scripts on rerenders.
 */
const SITE_URL = 'https://kewleventsolutions.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;
const DEFAULT_DESCRIPTION =
  'Event production, venue consulting, and the software behind both. One team of operators for venues, planners, and events teams that need the details handled.';

export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  canonicalPath,
  ogImage = DEFAULT_OG_IMAGE,
  noindex = false,
}) {
  const fullTitle = title
    ? `${title} — Kewl Event Solutions`
    : 'Kewl Event Solutions — Event Operations, Venue Consulting, and the Software Behind It';

  const canonicalUrl = canonicalPath
    ? `${SITE_URL}${canonicalPath.startsWith('/') ? '' : '/'}${canonicalPath}`
    : SITE_URL;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {noindex ? (
        <meta name="robots" content="noindex, nofollow, noarchive" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large" />
      )}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Kewl Event Solutions" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content="Kewl Event Solutions — Event Operations, Venue Consulting, and the Software Behind It" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}
